import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import { RootState, AppDispatch } from '../store';
import { saveTeamToSupabase, loadTeamFromSupabase, addNotification } from '../store/teamSlice';
import { Team } from '../types';

/**
 * Custom hook for managing team operations with Supabase integration
 * Provides automatic saving, loading, and synchronization between Redux and Supabase
 */
export const useTeamManagement = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, supabaseUser, isAuthenticated } = useAuth();
  const { team, isLoading, saveError, hasUnsavedChanges, lastSaved } = useSelector((state: RootState) => state.team);
  
  // Simplified save status tracking - only need isSaving locally
  const [saveStatus, setSaveStatus] = useState<{ isSaving: boolean; saveError: string | null }>({
    isSaving: false,
    saveError: null
  });

  /**
   * Load team data when user authenticates
   */
  // Removed automatic loading to prevent multiple instances from triggering duplicate notifications
  // Team loading should be handled in the main App component

  /**
   * Load team from Supabase (or localStorage fallback)
   */
  const loadTeam = useCallback(async () => {
    if (!user?.id) return;

    try {
      const result = await dispatch(loadTeamFromSupabase({ 
        userId: user.id,
        isSupabaseUser: !!supabaseUser
      })).unwrap();
      
      if (result.team) {
        setSaveStatus(prev => ({
          ...prev,
          saveError: null
        }));
        
        // Note: Success notification is handled by the Redux action if needed
      }
    } catch (error) {
      console.error('Failed to load team:', error);
      setSaveStatus(prev => ({
        ...prev,
        saveError: 'Failed to load team from cloud'
      }));
    }
  }, [dispatch, user?.id, supabaseUser]);

  /**
   * Save current team to Supabase (or localStorage fallback)
   */
  const saveTeam = useCallback(async (teamToSave?: Team, showSuccessNotification = false) => {
    if (!user?.id) {
      console.warn('Cannot save team: user not authenticated');
      return false;
    }

    const teamData = teamToSave || team;
    if (!teamData) {
      console.warn('Cannot save team: no team data');
      return false;
    }

    setSaveStatus(prev => ({ ...prev, isSaving: true, saveError: null }));

    try {
      const result = await dispatch(saveTeamToSupabase({ 
        userId: user.id, 
        team: teamData,
        isSupabaseUser: !!supabaseUser
      })).unwrap();
      
      setSaveStatus({
        isSaving: false,
        saveError: null
      });

      if (showSuccessNotification) {
        dispatch(addNotification({
          message: result.fallback 
            ? 'Team saved locally (offline mode)' 
            : 'Team saved to cloud successfully',
          type: 'success'
        }));
      }

      return true;
    } catch (error) {
      console.error('Failed to save team:', error);
      setSaveStatus(prev => ({
        ...prev,
        isSaving: false,
        saveError: error instanceof Error ? error.message : 'Failed to save team'
      }));
      return false;
    }
  }, [dispatch, user?.id, team, supabaseUser]);

  /**
   * Manual save with immediate feedback
   */
  const saveTeamNow = useCallback(async (teamToSave?: Team) => {
    return await saveTeam(teamToSave, true); // Manual save with success notification
  }, [saveTeam]);

  /**
   * Force reload team from server
   */
  const reloadTeam = useCallback(async () => {
    await loadTeam();
  }, [loadTeam]);

  /**
   * Create backup of current team (export as JSON)
   */
  const backupTeam = useCallback(() => {
    if (!team) return;
    
    try {
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        appId: 'dream11-soccer',
        teamData: team
      };
      
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${team.name.replace(/\s+/g, '_')}_backup_${new Date().toISOString().split('T')[0]}.json`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      dispatch(addNotification({
        message: 'Team backup downloaded successfully',
        type: 'success'
      }));
    } catch (error) {
      dispatch(addNotification({
        message: 'Failed to create team backup',
        type: 'error'
      }));
    }
  }, [team, dispatch]);

  return {
    // Team data
    team,
    isLoading,
    saveError,
    
    // Enhanced save status
    saveStatus,
    
    // Actions
    saveTeam: saveTeamNow,
    loadTeam,
    reloadTeam,
    backupTeam,
    
    // Status helpers
    hasUnsavedChanges,
    isSaving: saveStatus.isSaving,
    lastSaved,
    isAuthenticated,
    canSave: isAuthenticated && !!user?.id,
  };
};

export default useTeamManagement; 